/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { useOrganization } from "@/contexts/organization-context";
import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useClerkContext } from "@/hooks/auth/use-clerk-context";
import { createApiClient } from "@/lib/api/api-client";
import { QUERY_STALE_TIME } from "@/lib/constants/query-config";
import {
  type SubscriptionPlan,
  SUBSCRIPTION_PLANS,
  isTrialExpired,
  getTrialDaysRemaining,
} from "@/lib/constants/subscriptions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// ============================================================================
// SUBSCRIPTION HOOK
// ============================================================================

export interface CreateSubscriptionInput {
  plan: SubscriptionPlan;
  billingCycle: "monthly" | "annual";
  customerEmail: string;
  customerName: string;
}

export interface CreateCheckoutSessionInput {
  plan: SubscriptionPlan;
  billingCycle: "monthly" | "annual";
  successUrl: string;
  cancelUrl: string;
}

export interface CreateCustomerPortalInput {
  returnUrl: string;
}

// Define the subscription details type based on expected API response
interface SubscriptionDetails {
  subscription_status?:
    | "active"
    | "trialing"
    | "canceled"
    | "past_due"
    | "grace_period"
    | "incomplete"
    | "incomplete_expired"
    | "unpaid";
  subscription_plan?: string;
  subscription_cycle?: "monthly" | "annual";
  trial_end_date?: string;
  subscription_end_date?: string;
  subscription_canceled_at?: string;
  stripe_customer_id?: string;
  [key: string]: unknown;
}

export const useSubscription = () => {
  const { isLoaded, isSignedIn } = useClerkContext();
  const { getToken } = useAuthSafe();
  const { organizationId, isLoading: orgLoading } = useOrganization();
  const queryClient = useQueryClient();

  // ============================================================================
  // SUBSCRIPTION DETAILS
  // ============================================================================

  const {
    data: subscriptionDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
    refetch: refetchDetails,
  } = useQuery({
    queryKey: ["subscription", "details", organizationId ?? "no-org"],
    queryFn: async (): Promise<SubscriptionDetails> => {
      if (!isLoaded || !isSignedIn) {
        throw new Error("Authentication not ready");
      }
      if (!organizationId) {
        throw new Error("Organization not ready");
      }

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      });

      const res = await api.get<SubscriptionDetails>(
        "/api/subscriptions/details",
      );

      if (!res.ok) {
        throw new Error(res.message || "Failed to fetch subscription details");
      }

      return res.data;
    },
    staleTime: QUERY_STALE_TIME.STANDARD, // subscription is STANDARD
    enabled: isLoaded && isSignedIn && !orgLoading && !!organizationId,
    retry: (failureCount, error) => {
      if (
        error.message.includes("Authentication") ||
        error.message.includes("Organization")
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // ============================================================================
  // CREATE SUBSCRIPTION
  // ============================================================================

  const createSubscription = useMutation({
    mutationFn: async (input: CreateSubscriptionInput) => {
      if (!isLoaded || !isSignedIn) {
        throw new Error("Authentication not ready");
      }
      if (!organizationId) {
        throw new Error("Organization not ready");
      }

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      });

      const res = await api.post<unknown>("/api/subscriptions/create", input);

      if (!res.ok) {
        throw new Error(res.message || "Failed to create subscription");
      }

      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      toast.success("Subscription created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create subscription");
    },
  });

  // ============================================================================
  // CREATE CHECKOUT SESSION
  // ============================================================================

  const createCheckoutSession = useMutation({
    mutationFn: async (input: CreateCheckoutSessionInput) => {
      if (!isLoaded || !isSignedIn) {
        throw new Error("Authentication not ready");
      }
      if (!organizationId) {
        throw new Error("Organization not ready");
      }

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      });

      const res = await api.post<{ url?: string }>(
        "/api/subscriptions/checkout",
        input,
      );

      if (!res.ok) {
        throw new Error(res.message || "Failed to create checkout session");
      }

      return res.data;
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout (external URL)
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create checkout session");
    },
  });

  // ============================================================================
  // CREATE CUSTOMER PORTAL SESSION
  // ============================================================================

  const createCustomerPortalSession = useMutation({
    mutationFn: async (input: CreateCustomerPortalInput) => {
      if (!isLoaded || !isSignedIn) {
        throw new Error("Authentication not ready");
      }
      if (!organizationId) {
        throw new Error("Organization not ready");
      }

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      });

      const res = await api.post<{ url?: string }>(
        "/api/subscriptions/portal",
        input,
      );

      if (!res.ok) {
        throw new Error(
          res.message || "Failed to create customer portal session",
        );
      }

      return res.data;
    },
    onSuccess: (data) => {
      // Redirect to Stripe customer portal (external URL)
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create customer portal session");
    },
  });

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const isSubscriptionActive = () => {
    return (
      subscriptionDetails?.subscription_status === "active" ||
      subscriptionDetails?.subscription_status === "trialing"
    );
  };

  const isSubscriptionCanceled = () => {
    return subscriptionDetails?.subscription_status === "canceled";
  };

  const isSubscriptionPastDue = () => {
    return subscriptionDetails?.subscription_status === "past_due";
  };

  const isSubscriptionGracePeriod = () => {
    return subscriptionDetails?.subscription_status === "grace_period";
  };

  const getCurrentPlan = (): SubscriptionPlan => {
    const plan = subscriptionDetails?.subscription_plan;
    // Handle legacy free_org → trial_org migration
    if (!plan || plan === "free_org") {
      return SUBSCRIPTION_PLANS.TRIAL;
    }
    return plan as SubscriptionPlan;
  };

  const getBillingCycle = () => {
    return subscriptionDetails?.subscription_cycle ?? "monthly";
  };

  const getTrialEndDate = () => {
    return subscriptionDetails?.trial_end_date;
  };

  const getSubscriptionEndDate = () => {
    return subscriptionDetails?.subscription_end_date;
  };

  const hasStripeCustomer = () => {
    return !!subscriptionDetails?.stripe_customer_id;
  };

  /**
   * Check if the organization is on a trial.
   *
   * Two sources of truth:
   * 1. subscription_status === "trialing" — Stripe says the sub is in its trial period
   * 2. plan === TRIAL — legacy/free-only trial orgs (no Stripe subscription)
   *
   * A paid plan (e.g. starter_plan) with status "trialing" IS on trial — the
   * user chose "Free Trial" during onboarding and Stripe set trial_period_days.
   */
  const isOnTrial = () => {
    if (subscriptionDetails?.subscription_status === "trialing") return true;
    return getCurrentPlan() === SUBSCRIPTION_PLANS.TRIAL;
  };

  /**
   * Check if the organization has an active paid plan (not trial).
   * A subscription with status "trialing" is NOT considered a paid plan yet —
   * the user hasn't been charged. Once the trial ends and status becomes
   * "active", this returns true.
   */
  const isPaidPlan = () => {
    // If Stripe says we're trialing, the user hasn't paid yet
    if (subscriptionDetails?.subscription_status === "trialing") return false;

    const plan = getCurrentPlan();
    return (
      plan === SUBSCRIPTION_PLANS.STARTER ||
      plan === SUBSCRIPTION_PLANS.PROFESSIONAL ||
      plan === SUBSCRIPTION_PLANS.BUSINESS ||
      plan === SUBSCRIPTION_PLANS.MOBILE
    );
  };

  /**
   * Check if the trial has expired.
   * Active paid subscriptions (status "active") are never expired.
   * Only "trialing" or legacy TRIAL plan orgs can expire.
   */
  const hasTrialExpired = () => {
    if (subscriptionDetails?.subscription_status === "active") return false;
    if (!isOnTrial()) return false;
    return isTrialExpired(subscriptionDetails?.trial_end_date ?? null);
  };

  /**
   * Get days remaining in trial (0 if not on trial or expired)
   */
  const trialDaysRemaining = () => {
    if (!isOnTrial()) return 0;
    return getTrialDaysRemaining(subscriptionDetails?.trial_end_date ?? null);
  };

  /**
   * Check if user needs to select a plan (trial expired or no active subscription)
   */
  const requiresPlanSelection = () => {
    // Trial expired
    if (hasTrialExpired()) return true;
    // Subscription canceled or past due
    if (
      isSubscriptionCanceled() ||
      isSubscriptionPastDue() ||
      isSubscriptionGracePeriod()
    )
      return true;
    return false;
  };

  // ============================================================================
  // RETURN VALUES
  // ============================================================================

  return {
    // Data
    subscriptionDetails,
    isLoadingDetails,
    detailsError,

    // Mutations
    createSubscription: createSubscription.mutate,
    createSubscriptionAsync: createSubscription.mutateAsync,
    isCreatingSubscription: createSubscription.isPending,

    createCheckoutSession: createCheckoutSession.mutate,
    createCheckoutSessionAsync: createCheckoutSession.mutateAsync,
    isCreatingCheckoutSession: createCheckoutSession.isPending,

    createCustomerPortalSession: createCustomerPortalSession.mutate,
    createCustomerPortalSessionAsync: createCustomerPortalSession.mutateAsync,
    isCreatingCustomerPortalSession: createCustomerPortalSession.isPending,

    // Utility functions
    refetchDetails,
    isSubscriptionActive,
    isSubscriptionCanceled,
    isSubscriptionPastDue,
    isSubscriptionGracePeriod,
    getCurrentPlan,
    getBillingCycle,
    getTrialEndDate,
    getSubscriptionEndDate,
    hasStripeCustomer,

    // Trial-specific functions
    isOnTrial,
    isPaidPlan,
    hasTrialExpired,
    trialDaysRemaining,
    requiresPlanSelection,

    // Loading states
    isLoading: isLoadingDetails || orgLoading,
  };
};
